import { useEffect, useState } from 'react';
import type { ConVersion } from '../../../api/cliente';
import { claveIdempotencia } from '../../../api/comun';
import type { Operaciones } from '../../../api/operaciones';
import type { CambiosDatosLocales, Compromiso, FichaDeCliente, ProblemaCompromisos, Solicitud } from '../../../api/ops/clientesPrivacidad';
import { ErrorApi } from '../../../api/problema';
import { useAvisoBreve } from '../../../componentes/avisoBreve';
import { Ayuda } from '../../../componentes/Ayuda';
import { Boton } from '../../../componentes/Boton';
import { Campo } from '../../../componentes/Campo';
import { Casilla } from '../../../componentes/Casilla';
import { Chip } from '../../../componentes/Chip';
import { EstadoCargando } from '../../../componentes/EstadoPagina';
import { Icono } from '../../../componentes/Icono';
import { PanelLateral } from '../../../componentes/PanelLateral';
import { Ventana } from '../../../componentes/Ventana';
import { diaConHora, diaMes, diasHasta } from '../../../logica/fechasCortas';
import { textoDeError } from '../../equipo/textos';
import { AYUDA, ESTADO, N, PARTE, TEXTOS, TIPO, VIA, grupoDe, primerNombre, vence } from './textos';

type Pestana = 'solicitud' | 'partes';
type Modal = 'copia' | 'corregir' | 'suprimir' | 'ampliar' | 'rechazar' | null;

interface Props {
    ops: Operaciones;
    negocio: string;
    nombreNegocio: string;
    movil: boolean;
    soloLectura: boolean;
    solicitud: Solicitud | null;
    nombre: string;
    onCerrar: () => void;
    onCambio: (s: Solicitud) => void;
}

interface DatosLocales {
    nombre: string;
    telefono: string;
    email: string;
}

const MODULO: Record<string, 'users' | 'calendar-check' | 'ticket' | 'euro' | 'message-circle' | 'calendar-days' | 'layers'> = {
    clientes: 'users',
    reservas: 'calendar-check',
    'reservas-sala': 'calendar-check',
    entradas: 'ticket',
    'eventos-entradas': 'ticket',
    pagos: 'euro',
    canales: 'message-circle',
    citas: 'calendar-days',
};

function localesDe(f: FichaDeCliente): DatosLocales {
    return { nombre: f.local.display_name ?? '', telefono: f.local.phone ?? '', email: f.local.email ?? '' };
}

/**
 * Detalle de una solicitud (RV Panel de ficha, Solicitud · Partes; G151) con lo que el negocio puede hacer, solo en las
 * de `handled_by: business` (G150): enviar la copia, corregir sus datos locales y resolver, o suprimir. La supresión va
 * con ventana y casilla, sin «Deshacer» (G159): enseña lo que se cancela cuando la API responde
 * `409 commitments_pending` (G27) y ofrece «Bloquear por obligación legal o reclamación» (N7, G196). Ampliar (una vez,
 * dentro del primer mes, G199) y rechazar (con el aviso de la AEPD, N6) piden motivo.
 */
export function PanelSolicitud({ ops, negocio, nombreNegocio, movil, soloLectura, solicitud, nombre, onCerrar, onCambio }: Props) {
    const avisar = useAvisoBreve();
    const [vista, setVista] = useState<Solicitud | null>(null);
    const [detalle, setDetalle] = useState<Solicitud | null>(null);
    const [ficha, setFicha] = useState<ConVersion<FichaDeCliente> | null>(null);
    const [locales, setLocales] = useState<DatosLocales>({ nombre: '', telefono: '', email: '' });
    const [pestana, setPestana] = useState<Pestana>('solicitud');
    const [modal, setModal] = useState<Modal>(null);
    const [ocupado, setOcupado] = useState(false);
    const [motivo, setMotivo] = useState('');
    const [compromisos, setCompromisos] = useState<Compromiso[] | null>(null);
    const [deAcuerdo, setDeAcuerdo] = useState(false);
    const [bloquear, setBloquear] = useState(false);
    const [motivoBloqueo, setMotivoBloqueo] = useState('');
    const [bloqueada, setBloqueada] = useState(false);

    if (solicitud?.id !== vista?.id) {
        setVista(solicitud);
        setDetalle(null);
        setFicha(null);
        setPestana('solicitud');
        setModal(null);
        setCompromisos(null);
        setDeAcuerdo(false);
        setBloquear(false);
        setMotivoBloqueo('');
        setBloqueada(false);
    }

    const id = solicitud?.id ?? null;
    const clienteId = solicitud?.customer_id ?? null;
    const esCorreccion = solicitud?.type === 'rectification';

    // El detalle (partes, lo que escribió, cómo se comprobó) y, en una corrección, la ficha con su versión.
    useEffect(() => {
        if (id === null || clienteId === null) {
            return;
        }
        let vivo = true;
        ops.getPrivacyRequest(negocio, id)
            .then((d) => {
                if (vivo) {
                    setDetalle(d);
                }
            })
            .catch(() => undefined);
        if (esCorreccion) {
            ops.getCustomer(negocio, clienteId)
                .then((f) => {
                    if (vivo) {
                        setFicha(f);
                        setLocales(localesDe(f.datos));
                    }
                })
                .catch(() => undefined);
        }
        return () => {
            vivo = false;
        };
    }, [ops, negocio, id, clienteId, esCorreccion]);

    const s = detalle ?? solicitud;
    const abierta = s !== null && grupoDe(s.status) === 'abiertas';
    const delNegocio = s?.handled_by === 'business';
    const primero = primerNombre(nombre);
    const ahora = new Date();
    const dias = s === null ? 0 : diasHasta(vence(s), ahora);
    const puedeAmpliar = s !== null && delNegocio && abierta && s.status !== 'extended' && s.extended_due_at == null && s.status !== 'in_progress' && s.status !== 'blocked' && s.status !== 'scheduled' && new Date(s.due_at).getTime() >= ahora.getTime();
    const puedeRechazar = s !== null && delNegocio && abierta && s.status !== 'in_progress' && s.status !== 'blocked' && s.status !== 'scheduled';

    const cambios: CambiosDatosLocales = {};
    if (ficha !== null) {
        const antes = localesDe(ficha.datos);
        if (locales.nombre.trim() !== antes.nombre) {
            cambios.local_display_name = locales.nombre.trim() === '' ? null : locales.nombre.trim();
        }
        if (locales.telefono.trim() !== antes.telefono) {
            cambios.local_phone = locales.telefono.trim() === '' ? null : locales.telefono.trim();
        }
        if (locales.email.trim() !== antes.email) {
            cambios.local_email = locales.email.trim() === '' ? null : locales.email.trim();
        }
    }
    const diferencias: [string, string, string][] = ficha === null
        ? []
        : (
              [
                  ['Nombre', ficha.datos.local.display_name ?? '', locales.nombre.trim()],
                  ['Teléfono', ficha.datos.local.phone ?? '', locales.telefono.trim()],
                  ['Email', ficha.datos.local.email ?? '', locales.email.trim()],
              ] as [string, string, string][]
          ).filter(([, a, b]) => a !== b);

    const terminar = (r: Solicitud, aPartes: boolean) => {
        setModal(null);
        setDetalle(r);
        onCambio(r);
        if (aPartes) {
            setPestana('partes');
        }
    };

    const fallo = (e: unknown, porDefecto: string) => {
        avisar({ tipo: 'error', texto: textoDeError(e, porDefecto) });
    };

    const enviarCopia = () => {
        if (s === null) {
            return;
        }
        setOcupado(true);
        ops.executePrivacyRequest(negocio, s.id, false)
            .then((r) => { terminar(r, true); })
            .catch((e: unknown) => { fallo(e, 'No he podido enviar la copia. Prueba de nuevo.'); })
            .finally(() => { setOcupado(false); });
    };

    const corregir = () => {
        if (s === null || ficha === null || ficha.version === null || diferencias.length === 0) {
            return;
        }
        setOcupado(true);
        ops.updateCustomer(negocio, s.customer_id, cambios, ficha.version)
            .then((f) => {
                setFicha(f);
                return ops.executePrivacyRequest(negocio, s.id, false);
            })
            .then((r) => { terminar(r, false); })
            .catch((e: unknown) => { fallo(e, 'No he podido guardar los cambios. Prueba de nuevo.'); })
            .finally(() => { setOcupado(false); });
    };

    const suprimir = () => {
        if (s === null) {
            return;
        }
        setOcupado(true);
        // «Bloquear por obligación legal o reclamación» (G196): primero la marca; la supresión deja la ficha bloqueada.
        const marca = bloquear && !bloqueada ? ops.placeCustomerLegalHold(negocio, s.customer_id, motivoBloqueo.trim()).then(() => { setBloqueada(true); }) : Promise.resolve();
        marca
            .then(() => ops.executePrivacyRequest(negocio, s.id, s.cancellations_confirmed === true || (compromisos !== null && deAcuerdo), claveIdempotencia()))
            .then((r) => { terminar(r, true); })
            .catch((e: unknown) => {
                if (e instanceof ErrorApi && e.codigo === 'commitments_pending') {
                    setCompromisos((e.problema as ProblemaCompromisos).commitments ?? []);
                    setDeAcuerdo(false);
                    return;
                }
                fallo(e, 'No he podido suprimir sus datos. Prueba de nuevo.');
            })
            .finally(() => { setOcupado(false); });
    };

    const conMotivo = (accion: 'ampliar' | 'rechazar') => {
        if (s === null || motivo.trim().length < 10) {
            return;
        }
        setOcupado(true);
        (accion === 'ampliar' ? ops.extendPrivacyRequest(negocio, s.id, motivo.trim()) : ops.rejectPrivacyRequest(negocio, s.id, motivo.trim()))
            .then((r) => {
                terminar(r, false);
                avisar({ tipo: 'ok', texto: accion === 'ampliar' ? 'Plazo ampliado. Le avisamos con el motivo.' : 'Solicitud rechazada. Le avisamos con el motivo.' });
            })
            .catch((e: unknown) => { fallo(e, accion === 'ampliar' ? 'No he podido ampliar el plazo. Prueba de nuevo.' : 'No he podido rechazar la solicitud. Prueba de nuevo.'); })
            .finally(() => { setOcupado(false); });
    };

    // Acción del panel según el tipo (solo las del negocio y abiertas).
    const accion = (() => {
        if (s === null || !delNegocio) {
            return null;
        }
        if (s.type === 'access' && s.status === 'received') {
            return { texto: 'Enviar la copia', icono: 'send' as const, abrir: () => { setModal('copia'); } };
        }
        if (s.type === 'rectification' && abierta && s.status !== 'in_progress') {
            return { texto: 'Guardar y resolver', icono: 'check' as const, abrir: () => { setModal('corregir'); } };
        }
        if (s.type === 'erasure' && s.status === 'received') {
            return { texto: 'Revisar y suprimir', icono: 'user-round-x' as const, abrir: () => { setCompromisos(null); setDeAcuerdo(false); setModal('suprimir'); } };
        }
        return null;
    })();

    const lineas: [string, string, string | null][] = s === null
        ? []
        : [
              ['Qué pide', TIPO[s.type].nombre, null],
              ['Sobre qué', s.scope === 'account' ? 'Su Cuenta Reservaria: aquí se atiende su ficha en tu negocio' : `Su relación con ${nombreNegocio}`, null],
              ['Cómo llegó', VIA[s.channel], null],
              ['Quién la atiende', delNegocio ? 'Tu negocio' : 'Reservaria', null],
              ['Cómo se comprobó', s.identity_evidence_ref ?? '—', null],
              ...(s.type === 'erasure' ? ([['Cancelaciones', s.cancellations_confirmed === true ? (delNegocio ? 'Las aceptó' : 'Las aceptó al pedirla') : 'Se confirman con el cliente al suprimir', null]] as [string, string, null][]) : []),
              ['Recibida', `El ${diaMes(s.received_at, ahora)}`, null],
              ...(abierta
                  ? ([['Plazo', s.extended_due_at != null ? `Ampliado: vence el ${diaMes(s.extended_due_at, ahora)}` : `Vence el ${diaMes(s.due_at, ahora)} · ${dias === 1 ? 'mañana' : `en ${String(Math.max(0, dias))} días`}`, delNegocio && dias <= 10 ? 'aviso' : null]] as [string, string, string | null][])
                  : ([[s.status === 'rejected' ? 'Rechazada' : 'Resuelta', `El ${diaMes(s.resolved_at, ahora)}`, null]] as [string, string, null][])),
          ];

    const partes = s?.parts ?? [];
    const notaPartes = partes.length === 0 ? TEXTOS.partesVacias : partes.some((p) => p.status === 'failed') ? TEXTOS.partesFallo : s?.status === 'resolved' ? TEXTOS.partesHechas : TEXTOS.partesEnCurso;
    const textoSistema = s === null || delNegocio
        ? ''
        : s.scope === 'account'
          ? `${primero} pidió borrar su Cuenta Reservaria a Soporte de Reservaria. Su ficha en ${nombreNegocio} se anonimiza sola, módulo a módulo. No tienes que hacer nada; aquí ves cómo va.`
          : `${primero} la pidió desde «Mis datos» con su código${s.type === 'erasure' ? ' y aceptó lo que se cancela' : ''}. Cada módulo hace su parte solo y la solicitud se resuelve cuando terminan. No tienes que hacer nada; aquí ves cómo va.`;

    const vistaSolicitud = s === null ? null : (
        <div className="rv-ca-cuerpo">
            {textoSistema !== '' && (
                <div className="rv-ca-caja">
                    <span className="rv-ca-caja__fila">
                        <Icono nombre="info" />
                        <span>
                            <strong>{TEXTOS.laAtiendeReservaria}. </strong>
                            {textoSistema}
                        </span>
                    </span>
                </div>
            )}
            {soloLectura && delNegocio && abierta && (
                <p className="rv-ca-nota">
                    <Icono nombre="shield-check" />
                    <span>{TEXTOS.soloLecturaPanel}</span>
                </p>
            )}
            <div>
                <p className="rv-ca-ceja">Solicitud</p>
                {lineas.map(([k, v, tono]) => (
                    <div key={k} className="rv-ca-dato">
                        <span>{k}</span>
                        <strong className={tono === 'aviso' ? 'rv-ca-aviso-texto' : undefined}>{v}</strong>
                    </div>
                ))}
                <div className="rv-ca-dato">
                    <span>Estado</span>
                    <Chip estado={ESTADO[s.status][1]}>{ESTADO[s.status][0]}</Chip>
                </div>
            </div>
            {s.status === 'blocked' && (
                <p className="rv-ca-nota">
                    <Icono nombre="lock" />
                    <span>{TEXTOS.bloqueadaNota}</span>
                </p>
            )}
            {s.status === 'scheduled' && (
                <p className="rv-ca-nota">
                    <Icono nombre="clock" />
                    <span>{TEXTOS.programadaNota}</span>
                </p>
            )}
            {s.details != null && s.details !== '' && (
                <div className="rv-ca-caja">
                    <strong>{s.type === 'rectification' ? 'Qué pide corregir' : 'Lo que escribió'}</strong>
                    <span>{s.details}</span>
                </div>
            )}
            {s.status === 'rejected' && s.rejection_reason != null && (
                <div className="rv-ca-caja">
                    <strong>Motivo que recibió</strong>
                    <span>{s.rejection_reason}</span>
                </div>
            )}
            {delNegocio && s.type === 'access' && s.status === 'received' && (
                <div className="rv-pr-bloque">
                    <span className="rv-ca-baldosa">
                        <Icono nombre="download" />
                    </span>
                    <span className="rv-ca-fila__texto">
                        <strong>Qué recibe</strong>
                        <span>{TEXTOS.copiaQueRecibe}</span>
                    </span>
                </div>
            )}
            {delNegocio && s.type === 'erasure' && s.status === 'received' && (
                <div className="rv-pr-bloque">
                    <span className="rv-ca-baldosa">
                        <Icono nombre="user-round-x" />
                    </span>
                    <span className="rv-ca-fila__texto">
                        <strong>Qué pasa al suprimir</strong>
                        <span>{TEXTOS.suprimirQuePasa}</span>
                        <span>{TEXTOS.suprimirConserva}</span>
                    </span>
                </div>
            )}
            {delNegocio && s.type === 'rectification' && abierta && (
                <div className="rv-ca-bloque">
                    <div className="rv-ca-bloque__cab">
                        <strong>Datos de tu negocio</strong>
                        <span>{TEXTOS.corregirAyuda}</span>
                    </div>
                    {ficha === null ? (
                        <EstadoCargando seccion="su ficha" />
                    ) : (
                        <>
                            <Campo etiqueta="Nombre en tu negocio" valor={locales.nombre} onChange={(v) => { setLocales({ ...locales, nombre: v }); }} max={120} nombre="corregir-nombre" />
                            <Campo etiqueta="Teléfono" tipo="tel" valor={locales.telefono} onChange={(v) => { setLocales({ ...locales, telefono: v }); }} ayuda="Con prefijo, por ejemplo +34." nombre="corregir-telefono" />
                            <Campo etiqueta="Email" tipo="email" valor={locales.email} onChange={(v) => { setLocales({ ...locales, email: v }); }} ayuda="Déjalo vacío si no quiere email." nombre="corregir-email" />
                            {ficha.datos.account != null && (
                                <div className="rv-ca-bloque">
                                    <div className="rv-ca-bloque__cab">
                                        <strong>Su Cuenta Reservaria</strong>
                                        <span>{TEXTOS.corregirCuenta}</span>
                                    </div>
                                    {[
                                        ['Nombre', ficha.datos.account.display_name],
                                        ['Teléfono', ficha.datos.account.phone ?? '—'],
                                        ['Email', ficha.datos.account.email ?? '—'],
                                    ].map(([k, v]) => (
                                        <div key={k} className="rv-ca-dato">
                                            <span>{k}</span>
                                            <strong>{v}</strong>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            {(puedeAmpliar || puedeRechazar) && (
                <div className="rv-pr-mas">
                    {puedeAmpliar && (
                        <Boton tipo="secundario" icono="clock" movil={movil} onClick={() => { setMotivo(''); setModal('ampliar'); }}>
                            Ampliar el plazo
                        </Boton>
                    )}
                    {puedeRechazar && (
                        <Boton tipo="secundario" rojo icono="ban" movil={movil} onClick={() => { setMotivo(''); setModal('rechazar'); }}>
                            Rechazar
                        </Boton>
                    )}
                </div>
            )}
        </div>
    );

    const vistaPartes = (
        <div className="rv-ca-cuerpo">
            <div>
                {partes.map((p) => (
                    <div key={`${p.module}-${p.kind}`} className="rv-pr-parte">
                        <span className="rv-ca-baldosa">
                            <Icono nombre={MODULO[p.module.toLocaleLowerCase('es')] ?? 'layers'} />
                        </span>
                        <span className="rv-ca-fila__texto">
                            <strong>{p.module}</strong>
                            <span>{`${p.kind === 'export' ? 'Prepara su parte de la copia' : 'Borra sus datos y conserva lo obligatorio'}${p.records_count == null ? '' : ` · ${String(p.records_count)} ${p.records_count === 1 ? 'registro' : 'registros'}`}`}</span>
                        </span>
                        <Chip estado={PARTE[p.status][1]}>{PARTE[p.status][0]}</Chip>
                    </div>
                ))}
            </div>
            <p className="rv-ca-nota rv-ca-nota--gris">
                <Icono nombre="info" />
                <span>{notaPartes}</span>
            </p>
        </div>
    );

    const listaCompromisos = (lista: Compromiso[]) => (
        <div className="rv-ca-caja rv-ca-caja--aviso">
            <span className="rv-ca-caja__fila">
                <Icono nombre="triangle-alert" />
                <strong>{lista.length === 0 ? 'No tiene nada pendiente que cancelar' : 'Antes de suprimir: esto se cancela'}</strong>
            </span>
            {lista.map((c) => (
                <div key={`${c.module}-${c.kind}`} className="rv-pr-compromiso">
                    <strong>{`${c.module}: ${String(c.count)} ${c.kind}`}</strong>
                    {(c.items ?? []).map((item, i) => (
                        <span key={`${item.id}-${String(i)}`}>{[item.label ?? '', diaConHora(item.date)].filter((t) => t !== '').join(' · ')}</span>
                    ))}
                </div>
            ))}
        </div>
    );

    return (
        <>
            <PanelLateral
                abierto={solicitud !== null}
                onCerrar={onCerrar}
                movil={movil}
                tamano="ficha"
                titulo={nombre}
                eyebrow={s === null ? '' : TIPO[s.type].corto}
                subtitulo={s === null ? '' : `${VIA[s.channel]} · ${abierta ? `Vence el ${diaMes(vence(s), ahora)} · ${dias === 1 ? 'mañana' : `en ${String(Math.max(0, dias))} días`}` : `${s.status === 'rejected' ? 'Rechazada' : 'Resuelta'} el ${diaMes(s.resolved_at, ahora)}`}`}
                icono={s === null ? 'shield-check' : TIPO[s.type].icono}
                accion={accion?.texto ?? ''}
                accionIcono={accion?.icono ?? 'check'}
                onAccion={accion?.abrir}
            >
                <div className="rv-ca-cuerpo">
                    <div className="rv-pr-panel-cab">
                        <div role="tablist" aria-label="Apartados de la solicitud" className="rv-ca-botonera">
                            {(['solicitud', 'partes'] as const).map((p) => (
                                <button key={p} type="button" role="tab" aria-selected={pestana === p} className="rv-ca-botonera__b" onClick={() => { setPestana(p); }}>
                                    <Icono nombre={p === 'solicitud' ? 'file-text' : 'layers'} />
                                    {p === 'solicitud' ? 'Solicitud' : 'Partes'}
                                </button>
                            ))}
                        </div>
                        <Ayuda tarjetas={pestana === 'partes' ? AYUDA.partes : AYUDA.solicitud} />
                    </div>
                    {solicitud !== null && (pestana === 'solicitud' ? vistaSolicitud : vistaPartes)}
                </div>
            </PanelLateral>

            <Ventana abierta={modal === 'copia'} titulo={`Enviar la copia a ${nombre}`} texto={TEXTOS.copiaVentana} accion="Enviar la copia" accionIcono="send" ocupado={ocupado} ocupadoTexto="Enviando…" movil={movil} onAccion={enviarCopia} onCerrar={() => { setModal(null); }} />

            <Ventana
                abierta={modal === 'corregir'}
                titulo={`Corregir los datos de ${primero}`}
                texto={TEXTOS.corregirVentana}
                accion="Guardar y avisar"
                ocupado={ocupado}
                ocupadoTexto="Guardando…"
                inactiva={diferencias.length === 0}
                movil={movil}
                onAccion={corregir}
                onCerrar={() => { setModal(null); }}
            >
                {diferencias.length === 0 ? (
                    <p className="rv-ca-nota rv-ca-nota--gris">{TEXTOS.sinCambios}</p>
                ) : (
                    <div className="rv-ca-ventana-lineas">
                        {diferencias.map(([k, a, b]) => (
                            <div key={k} className="rv-ca-ventana-linea">
                                <span>{k}</span>
                                <strong>
                                    <span className="rv-pr-antes">{a === '' ? 'Vacío' : a}</span> → {b === '' ? 'Vacío' : b}
                                </strong>
                            </div>
                        ))}
                    </div>
                )}
            </Ventana>

            <Ventana
                abierta={modal === 'suprimir'}
                titulo={`Suprimir los datos de ${nombre}`}
                texto={N.n7(nombreNegocio)}
                accion="Suprimir"
                accionIcono="user-round-x"
                ocupado={ocupado}
                ocupadoTexto="Suprimiendo…"
                inactiva={(compromisos !== null && compromisos.length > 0 && !deAcuerdo) || (bloquear && !bloqueada && motivoBloqueo.trim().length < 3)}
                movil={movil}
                onAccion={suprimir}
                onCerrar={() => { setModal(null); }}
            >
                {compromisos !== null && (
                    <>
                        <div className="rv-ca-ventana-lineas">
                            {compromisos.map((c) => (
                                <div key={`${c.module}-${c.kind}`} className="rv-ca-ventana-linea">
                                    <span>Se cancela</span>
                                    <strong>{`${String(c.count)} ${c.kind}`}</strong>
                                </div>
                            ))}
                        </div>
                        {listaCompromisos(compromisos)}
                        {compromisos.length > 0 && (
                            <Casilla marcada={deAcuerdo} onCambio={setDeAcuerdo}>
                                {`${primero} sabe que se cancelan y está de acuerdo.`}
                            </Casilla>
                        )}
                    </>
                )}
                <Casilla marcada={bloquear || bloqueada} bloqueada={bloqueada} onCambio={setBloquear}>
                    {N.n7Bloquear}
                </Casilla>
                {(bloquear || bloqueada) && (
                    <>
                        <p className="rv-ca-nota rv-ca-nota--gris">{TEXTOS.bloquearAyuda}</p>
                        {!bloqueada && <Campo etiqueta={TEXTOS.bloquearMotivo} valor={motivoBloqueo} onChange={setMotivoBloqueo} ayuda={TEXTOS.bloquearMotivoAyuda} max={500} nombre="motivo-bloqueo" />}
                    </>
                )}
            </Ventana>

            <Ventana
                abierta={modal === 'ampliar'}
                titulo="Ampliar el plazo"
                texto={N.n6Ampliar}
                accion="Ampliar"
                accionIcono="clock"
                ocupado={ocupado}
                inactiva={motivo.trim().length < 10}
                movil={movil}
                onAccion={() => { conMotivo('ampliar'); }}
                onCerrar={() => { setModal(null); }}
            >
                <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} filas={3} max={500} ayuda={TEXTOS.motivoAyudaAmpliar} nombre="motivo-ampliar" />
            </Ventana>

            <Ventana
                abierta={modal === 'rechazar'}
                titulo="Rechazar la solicitud"
                texto={N.n6Rechazar(primero)}
                accion="Rechazar"
                accionIcono="ban"
                ocupado={ocupado}
                inactiva={motivo.trim().length < 10}
                movil={movil}
                onAccion={() => { conMotivo('rechazar'); }}
                onCerrar={() => { setModal(null); }}
            >
                <Campo etiqueta="Motivo" valor={motivo} onChange={setMotivo} filas={3} max={500} ayuda={TEXTOS.motivoAyudaRechazar} nombre="motivo-rechazar" />
            </Ventana>
        </>
    );
}
